/* eslint-disable react-hooks/set-state-in-effect */
/**
 * Firm-side Morning Brief editor.
 *
 * Left column: list of clients + today's brief status (missing/draft/published)
 * Right column: form to author the three insights, cash, health and coach.
 *
 * Phase 1: purely manual. Staff types the fields. Phase 3 will pre-fill this
 * form with AI proposals for staff to approve.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search, CheckCircle2, AlertOctagon, Plus, EyeOff, RefreshCw, ToggleLeft, ToggleRight, ShieldOff, ShieldCheck } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError, useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const STATUS_STYLES = {
    missing: "bg-[#F5F4EF] text-[#5E636E] border-[#E4E4E7]",
    draft: "bg-amber-50 text-amber-800 border-amber-200",
    published: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

const HEALTH_ROWS = [
    { key: "cash_flow", label: "Cash Flow" },
    { key: "profitability", label: "Profitability" },
    { key: "tax_readiness", label: "Tax Readiness" },
    { key: "growth", label: "Growth" },
    { key: "efficiency", label: "Efficiency" },
];

const EMPTY_INSIGHT = { title: "", why_it_matters: "", cta_label: "", cta_link: "" };

export default function FirmMorningBrief() {
    const { user } = useAuth();
    const isAdmin = user?.role === "firm_admin";
    const [rows, setRows] = useState([]);
    const [date, setDate] = useState("");
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("all"); // all | missing | draft | published
    const [accessFilter, setAccessFilter] = useState("all"); // all | enabled | disabled
    const [selectedId, setSelectedId] = useState(null);
    const [brief, setBrief] = useState(null);
    const [loadingList, setLoadingList] = useState(true);
    const [loadingBrief, setLoadingBrief] = useState(false);
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [togglingId, setTogglingId] = useState(null);
    const [bulkBusy, setBulkBusy] = useState(false);

    async function loadList() {
        setLoadingList(true);
        try {
            const { data } = await api.get("/morning-brief/today-status");
            setRows(data.rows || []);
            setDate(data.date || "");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoadingList(false);
        }
    }

    useEffect(() => { loadList(); }, []);

    // Load brief when a client is picked
    useEffect(() => {
        if (!selectedId) { setBrief(null); return; }
        setLoadingBrief(true);
        api.get("/morning-brief/current", { params: { client_id: selectedId } })
            .then((r) => setBrief(normalizeBrief(r.data)))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoadingBrief(false));
    }, [selectedId]);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return rows.filter((r) => {
            if (statusFilter !== "all" && r.brief_status !== statusFilter) return false;
            if (accessFilter === "enabled" && !r.morning_brief_enabled) return false;
            if (accessFilter === "disabled" && r.morning_brief_enabled) return false;
            if (needle && !r.client_name?.toLowerCase().includes(needle)) return false;
            return true;
        });
    }, [rows, q, statusFilter, accessFilter]);

    const counts = useMemo(() => ({
        total: rows.length,
        published: rows.filter((r) => r.brief_status === "published").length,
        draft: rows.filter((r) => r.brief_status === "draft").length,
        missing: rows.filter((r) => r.brief_status === "missing").length,
        enabled: rows.filter((r) => r.morning_brief_enabled).length,
    }), [rows]);

    async function toggleAccess(clientId, enabled) {
        if (!isAdmin) { toast.error("Admin only"); return; }
        setTogglingId(clientId);
        try {
            await api.patch(`/morning-brief/clients/${clientId}/access`, { enabled });
            toast.success(enabled
                ? "Enabled — client will see the Morning Brief hero on their portal home"
                : "Disabled — the Morning Brief hero is hidden from this client's portal home");
            setRows((rs) => rs.map((r) => (r.client_id === clientId ? { ...r, morning_brief_enabled: enabled } : r)));
        } catch (e) { toast.error(formatApiError(e)); } finally { setTogglingId(null); }
    }

    async function disableAll() {
        if (!isAdmin) { toast.error("Admin only"); return; }
        if (!window.confirm("Disable the Morning Brief for EVERY active client in this firm? Clients won't see the hero until you re-enable them individually.")) return;
        setBulkBusy(true);
        try {
            const { data } = await api.patch("/morning-brief/clients/access/bulk", { enabled: false, client_ids: null });
            toast.success(`Disabled for ${data.modified} of ${data.matched} clients`);
            loadList();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBulkBusy(false); }
    }

    async function enableAll() {
        if (!isAdmin) { toast.error("Admin only"); return; }
        if (!window.confirm("Enable the Morning Brief for EVERY active client in this firm? Any client with a published brief will start seeing the hero immediately.")) return;
        setBulkBusy(true);
        try {
            const { data } = await api.patch("/morning-brief/clients/access/bulk", { enabled: true, client_ids: null });
            toast.success(`Enabled for ${data.modified} of ${data.matched} clients`);
            loadList();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBulkBusy(false); }
    }

    async function persist(action) {
        if (!brief) return;
        const isPublish = action === "publish";
        if (isPublish) setPublishing(true); else setSaving(true);
        try {
            const payload = {
                client_id: selectedId,
                brief_date: date || undefined,
                priority: brief.priority,
                risk: brief.risk,
                opportunity: brief.opportunity,
                cash_balance: brief.cash_balance === "" ? null : Number(brief.cash_balance),
                cash_period_label: brief.cash_period_label || "Last 60 days",
                cash_series: parseSeries(brief.cash_series_text),
                health_score: brief.health_score === "" ? null : Number(brief.health_score),
                health_breakdown: brief.health_breakdown,
                coach_summary: brief.coach_summary,
            };
            const url = isPublish ? "/morning-brief/publish" : "/morning-brief/upsert";
            const { data } = await api.post(url, payload);
            setBrief(normalizeBrief(data));
            toast.success(isPublish ? "Published to client portal" : "Draft saved");
            loadList();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setPublishing(false);
            setSaving(false);
        }
    }

    async function unpublish() {
        if (!selectedId) return;
        try {
            await api.post("/morning-brief/unpublish", null, { params: { client_id: selectedId, brief_date: date } });
            toast.success("Reverted to draft — client won't see it until you re-publish");
            const { data } = await api.get("/morning-brief/current", { params: { client_id: selectedId } });
            setBrief(normalizeBrief(data));
            loadList();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    return (
        <div>
            <PageHeader
                eyebrow="Morning Brief"
                title="Today's 3 Things That Matter"
                description={`Write the priority, risk and opportunity clients see when they open the portal. ${date ? `(${date} NZ)` : ""} · ${counts.enabled} of ${counts.total} clients have this enabled.`}
                testId="morning-brief-header"
                actions={(
                    <div className="flex items-center gap-2">
                        {isAdmin && (
                            <>
                                <Button variant="outline" onClick={enableAll} disabled={bulkBusy} data-testid="brief-enable-all">
                                    {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Enable for all
                                </Button>
                                <Button variant="outline" onClick={disableAll} disabled={bulkBusy} data-testid="brief-disable-all">
                                    {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldOff className="h-4 w-4" />} Disable for all
                                </Button>
                            </>
                        )}
                        <Button variant="outline" onClick={loadList} data-testid="brief-refresh">
                            <RefreshCw className="h-4 w-4" /> Refresh
                        </Button>
                    </div>
                )}
            />

            <div className="grid gap-4 lg:grid-cols-[340px_1fr]">
                {/* --- LEFT: client list --- */}
                <div className="co-card">
                    <div className="p-3 border-b border-[#E4E4E7]">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5E636E]" />
                            <Input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search clients"
                                className="pl-9"
                                data-testid="brief-search"
                            />
                        </div>
                        <div className="mt-3 flex gap-1.5 flex-wrap">
                            <StatusChip label={`All ${counts.total}`} active={statusFilter === "all"} onClick={() => setStatusFilter("all")} testId="brief-filter-all" />
                            <StatusChip label={`Published ${counts.published}`} active={statusFilter === "published"} onClick={() => setStatusFilter("published")} tone="emerald" testId="brief-filter-published" />
                            <StatusChip label={`Draft ${counts.draft}`} active={statusFilter === "draft"} onClick={() => setStatusFilter("draft")} tone="amber" testId="brief-filter-draft" />
                            <StatusChip label={`Missing ${counts.missing}`} active={statusFilter === "missing"} onClick={() => setStatusFilter("missing")} testId="brief-filter-missing" />
                        </div>
                        <div className="mt-2 flex gap-1.5 flex-wrap">
                            <StatusChip label={`Access · All`} active={accessFilter === "all"} onClick={() => setAccessFilter("all")} testId="brief-access-all" />
                            <StatusChip label={`Enabled ${counts.enabled}`} active={accessFilter === "enabled"} onClick={() => setAccessFilter("enabled")} tone="emerald" testId="brief-access-enabled" />
                            <StatusChip label={`Disabled ${counts.total - counts.enabled}`} active={accessFilter === "disabled"} onClick={() => setAccessFilter("disabled")} testId="brief-access-disabled" />
                        </div>
                    </div>
                    <ul className="max-h-[70vh] overflow-y-auto divide-y divide-[#E4E4E7]" data-testid="brief-client-list">
                        {loadingList && (
                            <li className="p-6 text-sm text-[#5E636E]"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</li>
                        )}
                        {!loadingList && filtered.length === 0 && (
                            <li className="p-6 text-sm text-[#5E636E]">No clients match.</li>
                        )}
                        {!loadingList && filtered.map((r) => (
                            <li key={r.client_id} className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={() => setSelectedId(r.client_id)}
                                    className={`flex-1 min-w-0 text-left px-4 py-3 flex items-center gap-3 hover:bg-[#F5F4EF] ${selectedId === r.client_id ? "bg-[#F5F4EF]" : ""} ${r.morning_brief_enabled ? "" : "opacity-70"}`}
                                    data-testid={`brief-client-row-${r.client_id}`}
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium truncate text-[#111]">{r.client_name}</div>
                                        <div className="text-[11px] text-[#5E636E] truncate">
                                            {r.portal_segment || r.customer_type || "—"}
                                        </div>
                                    </div>
                                    <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${STATUS_STYLES[r.brief_status]}`}>
                                        {r.brief_status}
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); toggleAccess(r.client_id, !r.morning_brief_enabled); }}
                                    disabled={!isAdmin || togglingId === r.client_id}
                                    title={isAdmin ? (r.morning_brief_enabled ? "Hide Morning Brief from this client" : "Show Morning Brief to this client") : "Admin only"}
                                    className={`mr-2 shrink-0 grid place-items-center h-6 w-9 rounded-full transition-colors ${
                                        r.morning_brief_enabled ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-zinc-200 text-[#5E636E] hover:bg-zinc-300"
                                    } ${!isAdmin ? "cursor-not-allowed" : ""}`}
                                    data-testid={`brief-toggle-${r.client_id}`}
                                >
                                    {togglingId === r.client_id ? <Loader2 className="h-3 w-3 animate-spin" /> : r.morning_brief_enabled ? <ToggleRight className="h-3.5 w-3.5" /> : <ToggleLeft className="h-3.5 w-3.5" />}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* --- RIGHT: editor --- */}
                <div className="co-card co-card-pad">
                    {!selectedId && (
                        <div className="py-16 text-center text-sm text-[#5E636E]" data-testid="brief-empty">
                            Pick a client from the left to write their morning brief.
                        </div>
                    )}
                    {selectedId && loadingBrief && (
                        <div className="py-16 text-center text-sm text-[#5E636E]">
                            <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading brief…
                        </div>
                    )}
                    {selectedId && !loadingBrief && brief && (
                        <BriefForm
                            brief={brief}
                            setBrief={setBrief}
                            onSave={() => persist("save")}
                            onPublish={() => persist("publish")}
                            onUnpublish={unpublish}
                            saving={saving}
                            publishing={publishing}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

function StatusChip({ label, active, onClick, tone, testId }) {
    const toneCls = tone === "emerald" ? "border-emerald-200 text-emerald-800" : tone === "amber" ? "border-amber-200 text-amber-800" : "border-[#E4E4E7] text-[#5E636E]";
    return (
        <button
            type="button"
            onClick={onClick}
            className={`text-[11px] px-2 py-1 rounded-full border ${toneCls} ${active ? "bg-[#0E3B2E] !text-white !border-[#0E3B2E]" : "bg-white hover:bg-[#F5F4EF]"}`}
            data-testid={testId}
        >
            {label}
        </button>
    );
}

// -------- editor form ---------------------------------------------------

function BriefForm({ brief, setBrief, onSave, onPublish, onUnpublish, saving, publishing }) {
    const isPublished = brief.status === "published";
    return (
        <div className="space-y-6" data-testid="brief-form">
            <div className="flex items-center justify-between">
                <div>
                    <div className="co-label">Status</div>
                    <div className={`mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs border ${STATUS_STYLES[brief.status] || STATUS_STYLES.draft}`}>
                        {brief.status || "draft"}
                    </div>
                </div>
                <div className="flex gap-2">
                    {isPublished && (
                        <Button variant="outline" onClick={onUnpublish} data-testid="brief-unpublish">
                            <EyeOff className="h-4 w-4" /> Unpublish
                        </Button>
                    )}
                    <Button variant="outline" onClick={onSave} disabled={saving || publishing} data-testid="brief-save">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save draft"}
                    </Button>
                    <Button onClick={onPublish} disabled={saving || publishing} className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white" data-testid="brief-publish">
                        {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish to portal"}
                    </Button>
                </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
                <InsightEditor
                    label="Today's Priority"
                    Icon={Plus}
                    accent="emerald"
                    value={brief.priority}
                    onChange={(v) => setBrief({ ...brief, priority: v })}
                    testId="editor-priority"
                />
                <InsightEditor
                    label="Biggest Risk"
                    Icon={AlertOctagon}
                    accent="rose"
                    value={brief.risk}
                    onChange={(v) => setBrief({ ...brief, risk: v })}
                    testId="editor-risk"
                />
                <InsightEditor
                    label="Biggest Opportunity"
                    Icon={CheckCircle2}
                    accent="indigo"
                    value={brief.opportunity}
                    onChange={(v) => setBrief({ ...brief, opportunity: v })}
                    testId="editor-opportunity"
                />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
                <div className="co-card co-card-pad">
                    <div className="co-label">Cash Flow snapshot</div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                        <div>
                            <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Current balance (NZD)</Label>
                            <Input
                                type="number"
                                value={brief.cash_balance ?? ""}
                                onChange={(e) => setBrief({ ...brief, cash_balance: e.target.value })}
                                placeholder="12650"
                                className="mt-1.5"
                                data-testid="editor-cash-balance"
                            />
                        </div>
                        <div>
                            <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Period label</Label>
                            <Input
                                value={brief.cash_period_label || ""}
                                onChange={(e) => setBrief({ ...brief, cash_period_label: e.target.value })}
                                placeholder="Last 60 days"
                                className="mt-1.5"
                                data-testid="editor-cash-period"
                            />
                        </div>
                    </div>
                    <Label className="mt-3 block text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Chart data (label, amount) — one per line</Label>
                    <Textarea
                        value={brief.cash_series_text || ""}
                        onChange={(e) => setBrief({ ...brief, cash_series_text: e.target.value })}
                        placeholder={"15 Jun, 6800\n30 Jun, 7900\n15 Jul, 9200\n30 Jul, 12650"}
                        rows={6}
                        className="mt-1.5 font-mono text-xs"
                        data-testid="editor-cash-series"
                    />
                    <p className="mt-1.5 text-[11px] text-[#5E636E]">Comma-separated. First value shows on the left, last bar is highlighted.</p>
                </div>

                <div className="co-card co-card-pad">
                    <div className="co-label">Business Health Score</div>
                    <div className="mt-3">
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Overall score (0-100)</Label>
                        <Input
                            type="number"
                            min={0}
                            max={100}
                            value={brief.health_score ?? ""}
                            onChange={(e) => setBrief({ ...brief, health_score: e.target.value })}
                            placeholder="82"
                            className="mt-1.5"
                            data-testid="editor-health-score"
                        />
                    </div>
                    <div className="mt-3 space-y-2">
                        {HEALTH_ROWS.map((r) => (
                            <div key={r.key} className="flex items-center gap-3">
                                <Label className="flex-1 text-xs text-[#5E636E]">{r.label}</Label>
                                <Input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={brief.health_breakdown?.[r.key] ?? 0}
                                    onChange={(e) => setBrief({
                                        ...brief,
                                        health_breakdown: { ...brief.health_breakdown, [r.key]: Number(e.target.value) },
                                    })}
                                    className="w-24"
                                    data-testid={`editor-health-${r.key}`}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="co-card co-card-pad">
                <div className="co-label">Daybook Coach — morning summary</div>
                <Textarea
                    value={brief.coach_summary || ""}
                    onChange={(e) => setBrief({ ...brief, coach_summary: e.target.value })}
                    placeholder="I reviewed the books overnight. You are up $2,100, but two clients are 30+ days late…"
                    rows={4}
                    className="mt-2"
                    data-testid="editor-coach"
                />
                <p className="mt-1.5 text-[11px] text-[#5E636E]">Sets the tone. Keep it warm, specific and actionable — clients read this first thing.</p>
            </div>
        </div>
    );
}

function InsightEditor({ label, Icon, accent, value, onChange, testId }) {
    const dotCls = accent === "emerald" ? "bg-emerald-500" : accent === "rose" ? "bg-rose-500" : "bg-indigo-500";
    return (
        <div className="co-card co-card-pad" data-testid={testId}>
            <div className="flex items-center gap-2">
                <span className={`grid h-6 w-6 place-items-center rounded-full ${dotCls} text-white`}>
                    <Icon className="h-3.5 w-3.5" strokeWidth={3} />
                </span>
                <span className="text-xs font-semibold uppercase tracking-[0.14em]">{label}</span>
            </div>
            <div className="mt-3 space-y-3">
                <div>
                    <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Headline</Label>
                    <Input
                        value={value?.title || ""}
                        onChange={(e) => onChange({ ...value, title: e.target.value })}
                        placeholder="Send 2 overdue invoices worth $4,850"
                        className="mt-1.5"
                        data-testid={`${testId}-title`}
                    />
                </div>
                <div>
                    <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Why this matters</Label>
                    <Textarea
                        value={value?.why_it_matters || ""}
                        onChange={(e) => onChange({ ...value, why_it_matters: e.target.value })}
                        placeholder="Getting paid this week keeps you on track and covers next week's payroll."
                        rows={3}
                        className="mt-1.5"
                        data-testid={`${testId}-why`}
                    />
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">CTA label</Label>
                        <Input
                            value={value?.cta_label || ""}
                            onChange={(e) => onChange({ ...value, cta_label: e.target.value })}
                            placeholder="Take action"
                            className="mt-1.5"
                            data-testid={`${testId}-cta-label`}
                        />
                    </div>
                    <div>
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">CTA link</Label>
                        <Input
                            value={value?.cta_link || ""}
                            onChange={(e) => onChange({ ...value, cta_link: e.target.value })}
                            placeholder="/portal/requests"
                            className="mt-1.5"
                            data-testid={`${testId}-cta-link`}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

// ---- helpers -----------------------------------------------------------

function normalizeBrief(d) {
    // Coerce backend doc → form-friendly shape (adds cash_series_text)
    const seriesText = (d?.cash_series || [])
        .map((p) => `${p.label}, ${p.amount}`)
        .join("\n");
    return {
        ...d,
        priority: d?.priority || { ...EMPTY_INSIGHT },
        risk: d?.risk || { ...EMPTY_INSIGHT },
        opportunity: d?.opportunity || { ...EMPTY_INSIGHT },
        health_breakdown: d?.health_breakdown || { cash_flow: 0, profitability: 0, tax_readiness: 0, growth: 0, efficiency: 0 },
        cash_series_text: seriesText,
    };
}

function parseSeries(text) {
    if (!text) return [];
    const out = [];
    for (const raw of text.split(/\r?\n/)) {
        const line = raw.trim();
        if (!line) continue;
        const parts = line.split(",").map((s) => s.trim());
        if (parts.length < 2) continue;
        const amount = Number(parts[1]);
        if (!parts[0] || Number.isNaN(amount)) continue;
        out.push({ label: parts[0], amount });
    }
    return out;
}
